import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import MailIcon from '@assets/icons/mail.svg?react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@ui/button';
import { Input } from '@ui/input';
import { routesMap } from '@shared/lib';
import { z } from 'zod';

import { sendPasswordResetEmail } from '../../../services';
import { Success } from '../Success';

import styles from './request.module.css';

const requestSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email')
    .email('Enter a valid email'),
});

type RequestFormValues = z.infer<typeof requestSchema>;

export const Request = () => {
  const [serviceError, setServiceError] = useState('');
  const [sentEmail, setSentEmail] = useState('');
  const { mutateAsync: sendResetEmail, isPending } = useMutation({
    mutationFn: sendPasswordResetEmail,
  });
  const form = useForm<RequestFormValues>({
    resolver: zodResolver(requestSchema),
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: {
      email: '',
    },
  });

  const submitResetRequest = async ({ email }: RequestFormValues) => {
    setServiceError('');

    const response = await sendResetEmail({ email });

    if (!response.success) {
      setServiceError(response.error || 'Failed to send reset email');
      return;
    }

    setSentEmail(email);
  };

  if (sentEmail) {
    return (
      <Success
        onBack={() => {
          setSentEmail('');
          setServiceError('');
          form.clearErrors();
        }}
      />
    );
  }

  const error = form.formState.errors.email?.message || serviceError;

  return (
    <form
      className={styles.form}
      onSubmit={form.handleSubmit(submitResetRequest, () =>
        setServiceError(''),
      )}
    >
      <div className={styles.header}>
        <h1 className={styles.title}>Forgot Password?</h1>
        <p className={styles.subtitle}>
          Don&apos;t worry! It occurs. Enter your register email to receive
          password reset instruction.
        </p>
      </div>
      <div className={styles.fields}>
        <label>
          <span className={styles.label}>Email</span>
          <span className={styles.field}>
            <MailIcon className={styles.icon} aria-hidden />
            <Input
              className={styles.input}
              type="email"
              placeholder="Email"
              autoComplete="email"
              inputSize="lg"
              {...form.register('email')}
            />
          </span>
        </label>
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button className={styles.button} type="submit" disabled={isPending}>
          Yes, send me email
        </Button>
        <Link className={styles.link} to={routesMap.auth}>
          No, back to login
        </Link>
      </div>
    </form>
  );
};
